import { useState, type FormEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Building } from 'lucide-react';
import type { LoginCredentials } from '@pms/shared';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/utils';
import { isAxiosError } from 'axios';

/**
 * Two mutually-exclusive login identities (Hotel Groups — Slice 4):
 *   • 'email'    → today's { email, password } form, verbatim + default.
 *   • 'username' → { groupCode, username, password } for email-less staff.
 * The tab only picks which body shape is posted; the JWT/session handling and
 * the `mustChangePassword` → /set-password first-login fence are UNCHANGED.
 */
type LoginMode = 'email' | 'username';

interface FormState {
  email: string;
  groupCode: string;
  username: string;
  password: string;
}

interface FormErrors {
  email?: string;
  groupCode?: string;
  username?: string;
  password?: string;
  general?: string;
}

const EMPTY_FORM: FormState = { email: '', groupCode: '', username: '', password: '' };

/** Validate only the fields relevant to the active mode. */
function validateForm(mode: LoginMode, values: FormState): FormErrors {
  const errors: FormErrors = {};

  if (mode === 'email') {
    if (!values.email.trim()) {
      errors.email = 'Email address is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
      errors.email = 'Please enter a valid email address.';
    }
  } else {
    // Username mode: non-empty checks only — no email regex.
    if (!values.groupCode.trim()) {
      errors.groupCode = 'Group code is required.';
    }
    if (!values.username.trim()) {
      errors.username = 'Username is required.';
    }
  }

  if (!values.password) {
    errors.password = 'Password is required.';
  } else if (values.password.length < 8) {
    errors.password = 'Password must be at least 8 characters.';
  }

  return errors;
}

export function LoginPage() {
  const { login, isLoading } = useAuth();
  const location = useLocation();

  const [mode, setMode] = useState<LoginMode>('email');
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Partial<Record<keyof FormState, boolean>>>({});

  function switchMode(next: LoginMode) {
    if (next === mode) return;
    setMode(next);
    // Clear validation/errors on switch so the hidden mode's stale errors don't
    // leak into the newly-shown one. Field values are preserved.
    setErrors({});
    setTouched({});
  }

  function handleChange(field: keyof FormState) {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = { ...form, [field]: e.target.value };
      setForm(next);
      // Re-validate on change once the field has been touched
      if (touched[field]) {
        const fieldErrors = validateForm(mode, next);
        setErrors((prev) => ({ ...prev, [field]: fieldErrors[field], general: '' }));
      }
    };
  }

  function handleBlur(field: keyof FormState) {
    return () => {
      setTouched((prev) => ({ ...prev, [field]: true }));
      const fieldErrors = validateForm(mode, form);
      setErrors((prev) => ({ ...prev, [field]: fieldErrors[field] }));
    };
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();

    const validationErrors = validateForm(mode, form);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setTouched({ email: true, groupCode: true, username: true, password: true });
      return;
    }

    setErrors({});

    const credentials: LoginCredentials =
      mode === 'email'
        ? { email: form.email.trim(), password: form.password }
        : {
            groupCode: form.groupCode.trim(),
            username: form.username.trim(),
            password: form.password,
          };

    try {
      await login(credentials);
      // Navigation is handled inside useAuth.login()
    } catch (err) {
      let message = 'An unexpected error occurred. Please try again.';

      if (isAxiosError(err)) {
        const status = err.response?.status;
        const serverMsg = err.response?.data?.message;
        const serverMessage = Array.isArray(serverMsg) ? serverMsg.join('. ') : typeof serverMsg === 'string' ? serverMsg : null;

        if (serverMessage) {
          message = serverMessage;
        } else if (status === 401) {
          // Generic per-mode copy (no user/tenant enumeration).
          message =
            mode === 'email'
              ? 'Invalid email or password.'
              : 'Invalid group code, username or password.';
        } else if (status === 429) {
          message = 'Too many login attempts. Please wait and try again.';
        } else if (status != null && status >= 500) {
          message = 'Unable to connect to server. Please try again later.';
        } else if (!err.response) {
          message = 'Network error. Please check your connection.';
        }
      }

      setErrors({ general: message });
    }
  }

  // Redirect hint from location state (e.g. after session expiry)
  const sessionExpired =
    (location.state as { sessionExpired?: boolean } | null)?.sessionExpired === true;

  return (
    <div className="min-h-screen flex bg-slate-950">
      {/* ── Left decorative panel (hidden on mobile) ── */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 bg-gradient-to-br from-slate-900 to-slate-950 border-r border-slate-800">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary-600">
            <Building size={20} className="text-white" aria-hidden="true" />
          </div>
          <span className="text-xl font-bold text-white tracking-tight">BestAcco PMS</span>
        </div>

        <div>
          <blockquote className="text-slate-300 text-lg font-light leading-relaxed">
            "Managing short-stay properties at scale, made simple."
          </blockquote>
          <p className="mt-4 text-slate-500 text-sm">
            Reservations &middot; Channel Management &middot; Dynamic Pricing &middot; Operations
          </p>
        </div>

        <p className="text-slate-600 text-xs">
          &copy; {new Date().getFullYear()} BestAcco. All rights reserved.
        </p>
      </div>

      {/* ── Login form panel ── */}
      <div className="flex flex-1 items-center justify-center p-6">
        <div className="w-full max-w-sm">
          {/* Mobile brand */}
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-primary-600">
              <Building size={18} className="text-white" aria-hidden="true" />
            </div>
            <span className="text-lg font-bold text-white">BestAcco PMS</span>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-gray-900">Sign in</h1>
              <p className="mt-1 text-sm text-gray-500">
                Enter your credentials to access the dashboard.
              </p>
            </div>

            {/* Identity mode toggle */}
            <div
              role="tablist"
              aria-label="Sign-in method"
              className="mb-6 grid grid-cols-2 gap-1 rounded-lg bg-gray-100 p-1"
            >
              <button
                type="button"
                role="tab"
                id="login-tab-email"
                aria-selected={mode === 'email'}
                aria-controls="login-panel"
                onClick={() => switchMode('email')}
                disabled={isLoading}
                className={cn(
                  'rounded-md px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                  mode === 'email'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700',
                )}
              >
                Email
              </button>
              <button
                type="button"
                role="tab"
                id="login-tab-username"
                aria-selected={mode === 'username'}
                aria-controls="login-panel"
                onClick={() => switchMode('username')}
                disabled={isLoading}
                className={cn(
                  'rounded-md px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                  mode === 'username'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700',
                )}
              >
                Group code + Username
              </button>
            </div>

            {/* Session expired banner */}
            {sessionExpired && (
              <div
                role="status"
                className="mb-4 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5 text-sm text-amber-700"
              >
                Your session has expired. Please sign in again.
              </div>
            )}

            {/* General error */}
            {errors.general && (
              <div
                role="alert"
                aria-live="assertive"
                className="mb-4 flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2.5 text-sm text-red-700"
              >
                {errors.general}
              </div>
            )}

            <form
              id="login-panel"
              role="tabpanel"
              aria-labelledby={mode === 'email' ? 'login-tab-email' : 'login-tab-username'}
              onSubmit={handleSubmit}
              noValidate
              aria-label="Sign in form"
              className="space-y-5"
            >
              {mode === 'email' ? (
                <Input
                  label="Email address"
                  type="email"
                  name="email"
                  autoComplete="email"
                  value={form.email}
                  onChange={handleChange('email')}
                  onBlur={handleBlur('email')}
                  error={touched.email ? errors.email : undefined}
                  placeholder="you@example.com"
                  required
                  disabled={isLoading}
                />
              ) : (
                <>
                  <Input
                    label="Group code"
                    type="text"
                    name="groupCode"
                    autoComplete="organization"
                    autoCapitalize="none"
                    value={form.groupCode}
                    onChange={handleChange('groupCode')}
                    onBlur={handleBlur('groupCode')}
                    error={touched.groupCode ? errors.groupCode : undefined}
                    placeholder="ABCHOTELS"
                    required
                    disabled={isLoading}
                  />
                  <Input
                    label="Username"
                    type="text"
                    name="username"
                    autoComplete="username"
                    autoCapitalize="none"
                    value={form.username}
                    onChange={handleChange('username')}
                    onBlur={handleBlur('username')}
                    error={touched.username ? errors.username : undefined}
                    placeholder="reception"
                    required
                    disabled={isLoading}
                  />
                </>
              )}

              <Input
                label="Password"
                type="password"
                name="password"
                autoComplete="current-password"
                value={form.password}
                onChange={handleChange('password')}
                onBlur={handleBlur('password')}
                error={touched.password ? errors.password : undefined}
                placeholder="••••••••"
                required
                disabled={isLoading}
              />

              <Button
                type="submit"
                variant="primary"
                fullWidth
                size="lg"
                isLoading={isLoading}
                disabled={isLoading}
              >
                {isLoading ? 'Signing in…' : 'Sign in'}
              </Button>
            </form>
          </div>

          <p className="mt-6 text-center text-sm text-slate-400">
            Don&apos;t have an account?{' '}
            <Link
              to="/register"
              className="font-medium text-primary-400 hover:text-primary-300 transition-colors"
            >
              Register
            </Link>
          </p>
          <p className="mt-2 text-center text-sm text-slate-400">
            <Link
              to="/forgot-password"
              className="font-medium text-primary-400 hover:text-primary-300 transition-colors"
            >
              Forgot your password?
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
